/**
 * Guest landing page at ``/`` — first screen an unauthenticated visitor
 * sees. Two paths off:
 *
 *   - Enter address + tap "See my ballot" → ``/chat`` showing the ballot:
 *     the Ballot tab in tab nav mode, the BallotSheet at the ``full``
 *     detent in sheet nav mode. Address is saved to the guest cache
 *     (``useGuestAddress``) so the personalized ballot loads via
 *     ``/ballot/by-address`` once chat mounts.
 *   - Tap "Skip for now — just chat" → ``/chat`` on the Chat tab / with
 *     the sheet at its fully-closed rest state (no ``previewClosed`` bump).
 *   - "Use my location" (or a location the visitor already granted) → a
 *     county sample ballot: a public address in their county is saved as a
 *     ``county_preview`` guest address and opens like "See my ballot", with a
 *     tip on the ballot asking for the registered address (``useCountyPreview``).
 *
 * The "open full" / "skip" intent is passed via ``sessionStorage`` keys
 * the chat route consumes on mount. SessionStorage (not React Router
 * location state) so a refresh on ``/chat`` still applies the intent
 * once and only once, and so the "skipped" preference can persist for
 * the session (a user who chose Skip stays opted out of the address
 * prompt until they sign in or close the tab).
 *
 * Mostly guests see this. Exception: a signed-in user who deletes their last
 * saved ballot is dropped back here (embedded in the ballot pane), where the
 * address submit persists to their profile via ``/user/settings`` and rebuilds
 * their real ballot rather than writing to the guest cache.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useMatch, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import type { SxProps, Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import { EXTERNAL_URLS } from '@/constants/urls';
import { isEmbed } from '@/config/embed';
import { detectOs } from '@/utils/detectOs';
import StoreBadge from './StoreBadge';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import { HeaderLanguageSelector } from '../dashboard-layout/HeaderLanguageSelector';
import AutoCompleteAddressInput from '../signup/AutoCompleteAddressInput';
import useGuestAddress, {
  type CountyPreviewSource,
  type GuestAddress,
} from '@/hooks/useGuestAddress';
import useAnalytics from '@/hooks/useAnalytics';
import useCountyPreview from '@/hooks/useCountyPreview';
import { useAuth } from '../auth/AuthProvider';
import { useSettingsDialog } from '@/contexts/SettingsDialogContext';
import { LANDING_OPEN_FULL_FLAG, LANDING_SKIPPED_FLAG } from '../chat/ChatWindow';
import { useResearch } from '@/contexts/ResearchContext';
import {
  resolveAddressFromText,
  type CountyPreviewAddress,
  type ExactAddress,
  type SelectionResult,
} from '@/utils/placesApi';
import useApi from '@/hooks/useApi';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import {
  classifyUnselectedSubmit,
  createLandingAddressFunnel,
  invalidSubmitProperties,
  isConfidentMatch,
  typedInputProperties,
  type InvalidSubmitReason,
  type SeeBallotVia,
} from '@/utils/landingAddressSubmit';

interface AddressDetails {
  formatted_address: string;
  place_id: string;
  address_components: unknown[];
  geometry: { location: { lat: number; lng: number } };
}

// Tracks the ?address= value prefilled this page load to prevent re-submit on remount.
let prefilledAddressThisLoad: string | null = null;

type PrefillState = 'idle' | 'resolving' | 'ready' | 'failed';

// Field and button share one height and radius so the web path reads as one
// group without a card around it.
const FIELD_HEIGHT = 52;
const FIELD_RADIUS = '12px';
// Lift for the field and button so they read as one floating pair without a
// card around them. Rock-slate shadow under the white field, brand-blue shadow
// under the blue button; the focus ring is the brand blue at 35%.
const FIELD_SHADOW = `0 4px 12px ${primitiveColors.rockSlate}1A, 0 1px 2px rgba(0, 0, 0, 0.08)`;
const FIELD_SHADOW_FOCUS = `0 8px 24px ${primitiveColors.rockSlate}29`;
const FIELD_FOCUS_RING = `0 0 0 3px ${semanticColors.brandPrimary}59`;
const CTA_SHADOW = `0 4px 12px ${semanticColors.brandPrimary}4D`;

// Header controls: transparent fill, translucent keyline, white text.
const ghostButtonSx: SxProps<Theme> = {
  textTransform: 'none',
  borderRadius: '999px',
  // Shorthand so it beats the global ``MuiButton`` ``padding: 12px 24px``.
  padding: '0 12px',
  height: 36,
  minHeight: 36,
  fontSize: 12,
  // Spanish "Iniciar sesión / Registrarse" only clears the language chip at
  // 360px when the label drops a point.
  '@media (max-width: 374px)': { fontSize: 11 },
  fontWeight: 600,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  color: primitiveColors.white,
  backgroundColor: 'transparent',
  border: `1px solid ${semanticColors.overlayWhite25}`,
  boxShadow: 'none',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    backgroundColor: semanticColors.overlayWhite08,
    borderColor: semanticColors.overlayWhite50,
    boxShadow: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${primitiveColors.white}`,
    outlineOffset: '2px',
  },
};

const ghostSelectSx: SxProps<Theme> = {
  // This row is already centered, so the chip's toolbar top offset is cancelled.
  '&&': { marginTop: 0 },
  height: 36,
  minHeight: 36,
  fontSize: 12,
  color: primitiveColors.white,
  backgroundColor: 'transparent',
  boxShadow: `inset 0 0 0 1px ${semanticColors.overlayWhite25}`,
  '& .MuiSelect-select': {
    height: '36px !important',
    boxSizing: 'border-box',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '10px',
    // Room for the 18px glyph sitting 4px from the right edge.
    paddingRight: '26px !important',
  },
  '& .MuiSelect-icon': { right: '4px', fontSize: '18px', color: primitiveColors.white },
  '& .vm-lang-globe': { width: 16, height: 16, color: primitiveColors.white },
  '&:hover': {
    backgroundColor: semanticColors.overlayWhite08,
    boxShadow: `inset 0 0 0 1px ${semanticColors.overlayWhite50}`,
  },
  '&.Mui-focusVisible, &:has(:focus-visible)': {
    boxShadow: `inset 0 0 0 1px ${primitiveColors.white}, 0 0 0 2px ${primitiveColors.white}`,
  },
};

// White field floating on the dark ground: hairline edge and soft shadow at
// rest, brand ring and a deeper shadow on focus. 16px text keeps iOS Safari
// from zooming on focus; the placeholder is sized apart.
const fieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: FIELD_HEIGHT,
    borderRadius: FIELD_RADIUS,
    backgroundColor: primitiveColors.white,
    fontSize: 16,
    boxShadow: FIELD_SHADOW,
    transition: 'box-shadow 0.2s ease',
    '& fieldset': { borderColor: semanticColors.composerBorder },
    '&:hover fieldset': { borderColor: semanticColors.composerBorder },
    '&.Mui-focused': { boxShadow: `${FIELD_SHADOW_FOCUS}, ${FIELD_FOCUS_RING}` },
    '&.Mui-focused fieldset': { borderColor: semanticColors.brandPrimary, borderWidth: 1 },
    '&.Mui-error fieldset': { borderColor: semanticColors.danger },
  },
  '& input::placeholder': { fontSize: 15, color: semanticColors.textPlaceholder, opacity: 1 },
};

// The one saturated element on the screen. Rests on the deeper brand blue
// (#4A5BE8) rather than #5B6CFF: white 16px text needs 4.5:1, which that shade
// clears (5.3:1) and #5B6CFF does not (4.2:1). #5B6CFF is the hover state.
const primaryCtaSx: SxProps<Theme> = {
  mt: '12px',
  height: FIELD_HEIGHT,
  minHeight: FIELD_HEIGHT,
  padding: '0 16px',
  // ``!important`` because the theme's ``MuiButton`` radius otherwise wins.
  borderRadius: `${FIELD_RADIUS} !important`,
  fontSize: 16,
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: semanticColors.brandPrimaryHover,
  color: primitiveColors.white,
  boxShadow: CTA_SHADOW,
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': { backgroundColor: semanticColors.brandPrimary, boxShadow: CTA_SHADOW },
  '&.Mui-disabled': {
    backgroundColor: semanticColors.brandPrimaryHover,
    color: primitiveColors.white,
    opacity: 0.45,
    boxShadow: 'none',
  },
  '&:focus-visible': {
    outline: `2px solid ${primitiveColors.white}`,
    outlineOffset: '3px',
  },
};

const skipLinkSx = {
  display: { xs: 'flex', md: 'none' },
  mx: 'auto',
  mt: '12px',
  padding: '6px 8px',
  borderRadius: '6px',
  // A bare <button> takes the UA font; inherit DM Sans from the page instead.
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  color: semanticColors.overlayWhite70,
  '&:hover': { textDecoration: 'underline', color: primitiveColors.white },
  '&:focus-visible': {
    outline: `2px solid ${primitiveColors.white}`,
    outlineOffset: '2px',
  },
} satisfies SxProps<Theme>;

// The secondary path: a ghost button, quieter than the blue primary and louder
// than the skip link. 44px tall for a comfortable tap target.
const locationButtonSx: SxProps<Theme> = {
  mt: '12px',
  width: '100%',
  minHeight: 44,
  px: '16px',
  py: '8px',
  gap: '8px',
  borderRadius: FIELD_RADIUS,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.3,
  textAlign: 'center',
  color: primitiveColors.white,
  // White at 50% clears 3:1 against the ground; the keyline is the button's edge.
  border: `1px solid ${semanticColors.overlayWhite50}`,
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    backgroundColor: semanticColors.overlayWhite08,
    borderColor: primitiveColors.white,
  },
  '&:focus-visible': {
    outline: `2px solid ${primitiveColors.white}`,
    outlineOffset: '2px',
  },
  '&[aria-disabled="true"]': { cursor: 'progress' },
};

interface GuestLandingPageProps {
  /**
   * Rendered inside the mobile Ballot / My Info panel rather than as the
   * standalone ``/`` screen: fill the panel instead of the viewport, keep the
   * logo row but drop its sign-in and language controls (the app bar carries
   * them), and let "just chat" switch to the Chat tab.
   */
  embedded?: boolean;
  /**
   * Keep the sign-in and language controls in the logo row even when embedded.
   * Set where the embedded landing owns the whole phone screen with the app bar
   * hidden (the mobile tab layout's Ballot tab before any address exists), so
   * those controls stay reachable.
   */
  showTopControls?: boolean;
}

const GuestLandingPage = ({ embedded = false, showTopControls = false }: GuestLandingPageProps) => {
  const settingsDialog = useSettingsDialog();
  const navigate = useNavigate();
  const onChatRoute = useMatch('/chat/:id?') !== null;
  const { setActiveTab: setMobileTab } = useBallotSheet();
  const { address: guestAddress, setAddress } = useGuestAddress();
  const { track } = useAnalytics();
  const { signIn, isAuthenticated, user } = useAuth();
  const query = useApi();
  const { refreshRecords, beginBuilding } = useBallotRecords();
  const authedSubmitRef = useRef(false);
  const { t } = useTranslation('landing');
  const { t: tCommon } = useTranslation('common');

  // Which store this phone can install from. Only iOS and Android phones get
  // the banner; a desktop browser at a phone width has nothing to install.
  const appStore = useMemo(() => {
    if (isEmbed()) return null;
    const os = detectOs();
    if (os === 'ios') return { os, href: EXTERNAL_URLS.APP_STORE } as const;
    if (os === 'android') return { os, href: EXTERNAL_URLS.PLAY_STORE } as const;
    return null;
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState<AddressDetails | null>(null);
  // Button stays enabled even without a selection
  // (a11y: disabled buttons are unreachable by keyboard/SR).
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  // ?address= pre-fill: read from research context (sessionStorage-backed) rather than the live URL
  // because the /chat route strips query params on mount.
  const { address: urlAddress } = useResearch();
  const [prefillState, setPrefillState] = useState<PrefillState>('idle');
  const prefillStartedRef = useRef(false);
  const prefillBusy = prefillState === 'resolving';

  // Phones open the suggestion list upward: the field sits in the lower half
  // of the screen, so a list below it lands behind the on-screen keyboard.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [submitting, setSubmitting] = useState(false);
  // Set when a typed address was resolved to a match the voter must confirm;
  // the next tap submits it as ``confirmed_match``.
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const pendingSelectionRef = useRef<Promise<SelectionResult> | null>(null);
  const suggestionsCountRef = useRef(0);
  const funnel = useMemo(() => createLandingAddressFunnel(track), [track]);
  // Bumped on every edit. A submit that awaited a lookup drops its result when
  // the voter has edited the field since, so a stale match never overwrites
  // or stands in for what they typed.
  const inputVersionRef = useRef(0);

  // Shared submit path for every way an address gets confirmed;
  // ``via`` lets the funnel distinguish them.
  const submitAddress = useCallback(
    (details: AddressDetails, via: SeeBallotVia) => {
      // Signed-in user (deleted their last ballot): persist the address to their
      // profile and rebuild the real ballot. Refreshing the saved-ballots list
      // repopulates ``records``, swapping the pane back to the ballot.
      if (isAuthenticated) {
        if (authedSubmitRef.current) return;
        authedSubmitRef.current = true;
        // Show the ballot-loading skeleton right away: the settings write below
        // does a Democracy Works round-trip that can take several seconds, and
        // without this the landing just sits there looking idle until the
        // records refresh finally swaps the pane.
        beginBuilding();
        track('Landing See Ballot Clicked', { via, authed: true });
        void (async () => {
          try {
            await query('/user/settings', {
              method: 'POST',
              body: JSON.stringify({
                email: user?.email,
                address_payload: {
                  formatted_address: details.formatted_address,
                  address_components: details.address_components,
                },
              }),
            });
            await refreshRecords();
            if (onChatRoute) {
              if (embedded) setMobileTab('ballot');
            } else {
              navigate('/chat');
            }
          } catch {
            // Reconcile back to the true state (clears the loading flag; falls
            // back to the landing). The user can also set their address from Settings.
            await refreshRecords();
          } finally {
            authedSubmitRef.current = false;
          }
        })();
        return;
      }
      setAddress({
        formatted_address: details.formatted_address,
        address_components: details.address_components,
      });
      track('Landing See Ballot Clicked', { via });
      // On /chat already (embedded in the mobile panel or the desktop pane):
      // the address save alone swaps the landing for the ballot, so no
      // navigation is needed. The ballot the voter just asked for is what they
      // should land on, so open the Ballot tab explicitly — they may have
      // reached this entry from the Chat tab.
      if (onChatRoute) {
        if (embedded) setMobileTab('ballot');
        return;
      }
      sessionStorage.setItem(LANDING_OPEN_FULL_FLAG, '1');
      navigate('/chat');
    },
    [
      isAuthenticated,
      query,
      user?.email,
      refreshRecords,
      beginBuilding,
      setAddress,
      track,
      navigate,
      onChatRoute,
      embedded,
      setMobileTab,
    ]
  );

  const resolvePrefillAddress = useCallback(
    async (addr: string) => {
      setPrefillState('resolving');
      const details = await resolveAddressFromText(addr);
      if (details) {
        const addressDetails: AddressDetails = {
          formatted_address: details.formatted_address,
          place_id: details.place_id,
          address_components: details.address_components,
          geometry: details.geometry,
        };
        setSelected(addressDetails);
        setInputValue(details.formatted_address);
        setErrorMessage(null);
        setPrefillState('ready');
        track('Landing Address Prefilled', { source: 'research_link' });
        // Streamlined flow: skip the manual "See my ballot" click.
        submitAddress(addressDetails, 'auto_prefill');
      } else {
        setPrefillState('failed');
        setErrorMessage(t('errorPrefillFailed'));
        track('Landing Address Prefill Failed', { source: 'research_link' });
      }
    },
    [track, submitAddress, t]
  );

  const startPrefill = useCallback(
    (addr: string) => {
      setInputValue(addr);
      void resolvePrefillAddress(addr);
    },
    [resolvePrefillAddress]
  );

  // One-shot trigger: prefill when a fresh ?address= is present and the field is untouched.
  useEffect(() => {
    if (!urlAddress) return;
    if (prefillStartedRef.current) return;
    if (prefilledAddressThisLoad === urlAddress) return; // already handled this load (e.g. remount)
    if (selected || inputValue.trim().length > 0) return; // user already typing

    prefillStartedRef.current = true;
    prefilledAddressThisLoad = urlAddress;
    startPrefill(urlAddress);
    // ``selected``/``inputValue`` excluded so this stays one-shot rather than re-firing on typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlAddress, startPrefill]);

  const handleSignIn = useCallback(() => {
    track('Landing Sign In Clicked');
    signIn();
  }, [track, signIn]);

  const handleAddressChange = useCallback(
    (value: string) => {
      // A real keystroke resets the prefill status back to idle so the
      // screen-reader live region and the faded button don't reflect a
      // stale auto-fill result the user has since edited away.
      inputVersionRef.current += 1;
      if (prefillState !== 'idle') setPrefillState('idle');
      setInputValue(value);
      // Editing after selecting invalidates the selection — the user
      // must re-pick from the autocomplete list before submission.
      if (selected && value !== selected.formatted_address) {
        setSelected(null);
        setAwaitingConfirmation(false);
      }
      pendingSelectionRef.current = null;
      // Any typing dismisses the previous error so it stops being read
      // out as the user iterates.
      if (errorMessage) setErrorMessage(null);
    },
    [selected, errorMessage, prefillState]
  );

  const handleAddressSelect = useCallback((details: AddressDetails) => {
    setSelected(details);
    setInputValue(details.formatted_address);
    setAwaitingConfirmation(false);
    setErrorMessage(null);
  }, []);

  const handleSuggestionsChange = useCallback(
    (count: number) => {
      suggestionsCountRef.current = count;
      funnel.suggestionsChanged(count);
    },
    [funnel]
  );

  const handleSelectionPending = useCallback((pending: Promise<SelectionResult>) => {
    pendingSelectionRef.current = pending;
    // A failed lookup is forgotten, so the next submit retries by resolving the
    // typed text. A non-US address is kept until the field changes: retrying
    // can't fix it, and the submit should report it as such.
    void pending.then(({ failure }) => {
      if (failure === 'lookup_failed' && pendingSelectionRef.current === pending) {
        pendingSelectionRef.current = null;
      }
    });
  }, []);

  // Set while ``reject`` moves focus, so its refocus isn't counted as the
  // visitor reaching for the field.
  const refocusingRef = useRef(false);
  const handleAddressFocus = useCallback(() => {
    if (refocusingRef.current) return;
    funnel.focused();
  }, [funnel]);

  const reject = useCallback(
    (reason: InvalidSubmitReason, message: string) => {
      setErrorMessage(message);
      // Send focus back so keyboard/SR users can correct without hunting.
      refocusingRef.current = true;
      addressInputRef.current?.focus();
      refocusingRef.current = false;
      track(
        'Landing See Ballot Invalid Submit',
        invalidSubmitProperties(reason, inputValue, suggestionsCountRef.current)
      );
    },
    [inputValue, track]
  );

  const handleSeeBallot = useCallback(async () => {
    if (submitting) return;
    if (selected) {
      submitAddress(selected, awaitingConfirmation ? 'confirmed_match' : 'click');
      return;
    }
    setSubmitting(true);
    const version = inputVersionRef.current;
    const isStale = () => inputVersionRef.current !== version;
    try {
      // A suggestion was tapped and its lookup is still in flight (or failed).
      if (pendingSelectionRef.current) {
        const { details, failure } = await pendingSelectionRef.current;
        if (isStale()) return;
        if (details) submitAddress(details, 'awaited_selection');
        else {
          const fallback =
            failure === 'unsupported_address' ? 'addressNotSupported' : 'addressLookupFailed';
          reject(failure, errorMessage ?? t(fallback, { ns: 'common' }));
        }
        return;
      }
      const step = classifyUnselectedSubmit({
        text: inputValue,
        prefillFailed: prefillState === 'failed',
      });
      if (step === 'empty') return reject('empty', t('errorNoAddress'));
      if (step === 'prefill_failed') return reject('prefill_failed', t('errorPrefillFailed'));
      if (step === 'needs_street_address') {
        return reject('needs_street_address', t('errorNeedStreet'));
      }
      // A failed Places call is a lookup failure, not a missing address.
      let lookupErrored = false;
      const resolved = await resolveAddressFromText(inputValue, (error) => {
        lookupErrored = true;
        funnel.lookupFailed(error);
      });
      if (isStale()) return;
      if (!resolved && lookupErrored) {
        return reject('lookup_failed', t('addressLookupFailed', { ns: 'common' }));
      }
      if (!resolved) return reject('no_match', t('errorNoMatch'));
      const details: AddressDetails = {
        formatted_address: resolved.formatted_address,
        place_id: resolved.place_id,
        address_components: resolved.address_components,
        geometry: resolved.geometry,
      };
      if (isConfidentMatch(inputValue, resolved)) {
        submitAddress(details, 'resolved_text');
        return;
      }
      // Show the match in the field and let the voter confirm it with a second
      // tap. Focus stays put: on a phone refocusing would reopen the keyboard
      // over a prompt that needs only a tap.
      setInputValue(details.formatted_address);
      setSelected(details);
      setAwaitingConfirmation(true);
      setErrorMessage(t('confirmResolvedAddress'));
      track(
        'Landing Address Confirmation Shown',
        typedInputProperties(inputValue, suggestionsCountRef.current)
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    selected,
    awaitingConfirmation,
    inputValue,
    prefillState,
    errorMessage,
    submitAddress,
    reject,
    funnel,
    track,
    t,
  ]);

  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void handleSeeBallot();
    },
    [handleSeeBallot]
  );

  const handleSkip = useCallback(() => {
    track('Landing Skip Clicked');
    // Inside the mobile ballot panel (``embedded`` on the chat route) the chat
    // is one tab over, so switch to it. Standalone, the regular /chat route is
    // the destination: in sheet mode guest + no address + new chat triggers
    // ``previewClosed``, which lifts the sheet to a small rest band exposing
    // the address-entry input for users who change their mind without
    // re-routing back to the landing. The skip flag tells the chat route this
    // arrival chose to skip, so tab mode opens on Chat instead of auto-opening
    // the Ballot tab's address entry; consumed once there.
    if (embedded && onChatRoute) {
      setMobileTab('chat');
      return;
    }
    sessionStorage.setItem(LANDING_SKIPPED_FLAG, '1');
    navigate('/chat');
  }, [track, navigate, embedded, onChatRoute, setMobileTab]);

  // County preview: the device's location (or, with the IP guess on, the
  // visitor's IP) becomes a public address in its county, whose ballot stands
  // in until the voter enters their own. The ballot then carries a tip asking
  // for the registered address. Guests only, and not in partner embeds, whose
  // iframes don't grant geolocation.
  const typedAddressRef = useRef('');
  useEffect(() => {
    typedAddressRef.current = inputValue;
  }, [inputValue]);
  const applyLocatedAddress = useCallback(
    (address: GuestAddress) => {
      // The voter is already entering their own address; keep theirs.
      if (typedAddressRef.current.trim()) return false;
      if (document.activeElement === addressInputRef.current) return false;
      setAddress(address);
      if (onChatRoute) {
        if (embedded) setMobileTab('ballot');
        return true;
      }
      sessionStorage.setItem(LANDING_OPEN_FULL_FLAG, '1');
      navigate('/chat');
      return true;
    },
    [setAddress, onChatRoute, embedded, setMobileTab, navigate]
  );
  const handleCountyPreview = useCallback(
    (preview: CountyPreviewAddress, source: CountyPreviewSource) =>
      applyLocatedAddress({
        formatted_address: preview.formatted_address,
        address_components: preview.address_components,
        county_preview: { jurisdiction: preview.jurisdiction, state: preview.state, source },
      }),
    [applyLocatedAddress]
  );
  // A precise fix's street address is taken as the voter's own, like a picked
  // suggestion, so the ballot shows no preview tip, chip, or banner.
  const handleExactAddress = useCallback(
    (address: ExactAddress) =>
      applyLocatedAddress({
        formatted_address: address.formatted_address,
        address_components: address.address_components,
      }),
    [applyLocatedAddress]
  );
  const countyPreview = useCountyPreview({
    enabled:
      !isEmbed() &&
      !isAuthenticated &&
      !urlAddress &&
      !guestAddress?.formatted_address &&
      !submitting,
    ipGuess: true,
    onPreview: handleCountyPreview,
    onExactAddress: handleExactAddress,
  });
  const locating = countyPreview.status === 'locating';
  // Progress and retryable failures read out below the button, which keeps its
  // name and focus throughout.
  const locationMessage = locating
    ? t('locationFinding')
    : countyPreview.status === 'failed'
      ? t('locationFailed')
      : null;
  const locationButtonRef = useRef<HTMLButtonElement>(null);
  const locationButtonFocusedRef = useRef(false);
  const handleUseLocation = useCallback(() => {
    if (locating) return;
    locationButtonFocusedRef.current = document.activeElement === locationButtonRef.current;
    countyPreview.requestLocation();
  }, [locating, countyPreview]);
  // A denial removes the button, so its message moves to the address field's
  // own slot (above the field on phones, clear of the keyboard), and focus
  // follows when it was on the button.
  useEffect(() => {
    if (countyPreview.failure !== 'denied') return;
    setErrorMessage(t('locationDenied'));
    if (locationButtonFocusedRef.current) addressInputRef.current?.focus();
  }, [countyPreview.failure, t]);

  // On phones the message sits above the field: an error refocuses the field,
  // and the keyboard that opens covers anything below it.
  const addressMessage = errorMessage && (
    <Typography
      id="landing-address-error"
      // "Is this your address?" is a question: announced politely, not as an alert.
      role={awaitingConfirmation ? 'status' : 'alert'}
      sx={{
        ...(isPhone ? { mb: '8px' } : { mt: '8px' }),
        fontSize: 13,
        fontWeight: 500,
        // "Is this your address?" is a question, not an error.
        color: awaitingConfirmation ? primitiveColors.white : semanticColors.errorOnDark,
      }}
    >
      {errorMessage}
    </Typography>
  );

  return (
    <Box
      sx={{
        // Standalone (mobile ``/`` route): fill the viewport.
        // Embedded (desktop right pane at md+, or the mobile ballot panel via
        // ``embedded``): fill the parent container. ``minHeight: 0`` lets
        // ``height: 100%`` take full effect without the 100dvh floor
        // fighting it.
        minHeight: embedded ? 0 : { xs: '100dvh', md: 0 },
        height: embedded ? '100%' : { md: '100%' },
        // Cap to the viewport at xs so this Box — not the document — owns the
        // scroll. ``index.html`` locks ``html, body`` to ``overflow: hidden``
        // so the on-screen keyboard can't scroll the app bar away, which means
        // any surface that overflows without its own ``auto`` is unreachable
        // rather than scrollable. That bites hardest in a partner embed, where
        // the iframe height is fixed by the host page and is routinely shorter
        // than this screen.
        maxHeight: embedded ? '100%' : { xs: '100dvh' },
        overflowY: 'auto',
        // ``position: relative`` anchors the absolutely-positioned
        // footer (embedded md+ only) to this Box's bottom edge.
        position: 'relative',
        backgroundColor: semanticColors.ballotElectionCard,
        display: 'flex',
        flexDirection: 'column',
        // 8px scale throughout: 16px page gutter, 32px between blocks, 12px
        // between related elements.
        px: '16px',
        pt: '12px',
        // The footer pads for the home indicator itself, so the inset isn't
        // added twice under the privacy link.
        pb: 0,
        color: primitiveColors.white,
      }}
    >
      {/* Top row: logo + ghost controls. The logo shows on every surface; the
          controls are the dashboard header's job wherever one is above this
          screen — at md+, and inside the mobile ballot panel, where the logo
          stays because it is the panel's header there. The exception is the
          full-screen landing, which hides the app bar and so carries them
          itself. They are ghosts on purpose — two solid white pills up here
          fought the headline for attention. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          flexWrap: 'nowrap',
          gap: 1,
          // Full width at every breakpoint: the mark sits in the surface's top
          // corner, on the page gutter, rather than tracking the centered
          // content column below it.
          width: '100%',
          // Matches the footer's top padding, so the centered column below has
          // equal space above and below it.
          mb: '16px',
        }}
      >
        {/* The mark alone brands this screen on every surface — no wordmark,
            at any breakpoint. At md+ (desktop right pane) the dashboard header
            sits over the LEFT pane only, since its width is capped to the chat
            half while the ballot is open, so without this the pane carried no
            VoteMate mark at all. */}
        <Box sx={{ flexShrink: 0, display: 'flex' }}>
          <VotemateLogo size={26} logoOnly />
        </Box>
        {/* Sign In and the language switch are the dashboard header's job at
            md+, so this row shows them on the phone layout only — where both
            the partner embed and the mobile ``/`` route land here before any
            dashboard header exists, and without them a Spanish speaker has no
            way to switch until the next screen. Embedded in the mobile ballot
            panel the app bar carries them, so the row goes entirely — except
            on the full-screen landing (``showTopControls``), which hides that
            bar and is the only thing left holding them. */}
        {(!embedded || showTopControls) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexShrink: 0, display: { xs: 'flex', md: 'none' } }}
          >
            <Button disableRipple onClick={handleSignIn} size="small" sx={ghostButtonSx}>
              {t('signInSignUp')}
            </Button>
            <HeaderLanguageSelector sx={ghostSelectSx} />
          </Stack>
        )}
      </Box>

      {/* Content column, vertically centered in the space left between the
          pinned header and the bottom-anchored footer, so no dead zone opens
          above the footer. ``min-content`` at xs keeps the column from
          compressing below its content on short viewports so the root scrolls
          instead of clipping the headline (see the root's overflow). */}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: 'min-content', md: 0 },
          // Phones: sits the headline a little lower than dead center, clear
          // of the top row's controls.
          pt: { xs: '24px', md: 0 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Headline + one muted trust line */}
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: 44,
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              color: primitiveColors.white,
            }}
          >
            {t('hero')}
          </Typography>
          <Typography
            sx={{ mt: '12px', fontSize: 14, fontWeight: 600, color: semanticColors.overlayWhite70 }}
          >
            {`${t('trustFree')} · ${t('trustPrivate')} · ${t('trustNonpartisan')}`}
          </Typography>
        </Box>

        {/* App path — phones only, and only on an OS with a store. Hidden at
            md+ (the desktop pane has no store) and in partner embeds. The badge
            is deliberately the smaller of the two CTAs; the blue web button is
            the primary. */}
        {appStore && (
          <Box
            sx={{ width: '100%', maxWidth: 480, mt: '32px', display: { xs: 'block', md: 'none' } }}
          >
            <StoreBadge
              store={appStore.os}
              href={appStore.href}
              onClick={() =>
                track('Landing App Download Clicked', { source: 'landing_cta', os: appStore.os })
              }
            />
            <Box
              aria-hidden="true"
              sx={{ display: 'flex', alignItems: 'center', gap: '12px', my: '24px' }}
            >
              <Box
                sx={{ flex: 1, height: '1px', backgroundColor: semanticColors.overlayWhite15 }}
              />
              <Typography
                component="span"
                sx={{ fontSize: 13, fontWeight: 500, color: semanticColors.overlayWhite60 }}
              >
                {t('orContinueWeb')}
              </Typography>
              <Box
                sx={{ flex: 1, height: '1px', backgroundColor: semanticColors.overlayWhite15 }}
              />
            </Box>
          </Box>
        )}

        {/* Web path: label, address field, and the one saturated element on
            the screen — the blue primary button — set directly on the dark
            ground rather than in a white card. */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            mt: { xs: appStore ? 0 : '32px', md: '32px' },
          }}
        >
          {/* A form so the keyboard's Go key submits through the same path as
              the button. */}
          <Box component="form" noValidate onSubmit={handleFormSubmit}>
            <Typography
              id="landing-address-label"
              sx={{
                mb: '12px',
                fontSize: 14,
                lineHeight: 1.45,
                color: semanticColors.overlayWhite70,
              }}
            >
              {t('subhead')}
            </Typography>
            {isPhone && addressMessage}
            <AutoCompleteAddressInput
              value={inputValue}
              onChange={handleAddressChange}
              onAddressSelect={handleAddressSelect}
              placeholder={t('addressPlaceholder')}
              ariaLabel={t('addressAriaLabel')}
              size="small"
              inputRef={addressInputRef}
              error={Boolean(errorMessage) && !awaitingConfirmation}
              sx={fieldSx}
              dropdownDirection={isPhone ? 'above' : 'below'}
              enterKeyHint="go"
              onSuggestionsChange={handleSuggestionsChange}
              onSelectionPending={handleSelectionPending}
              onSelectionError={setErrorMessage}
              onFocus={handleAddressFocus}
              hideSuggestions={Boolean(errorMessage)}
              ariaDescribedBy={errorMessage ? 'landing-address-error' : undefined}
              onLookupError={funnel.lookupFailed}
            />
            {!isPhone && addressMessage}
            {/* Screen-reader narration of the pre-fill so non-sighted users
              get the same feedback as the visible state. The auto-submit
              path navigates away on 'ready', so 'Loading your ballot.' is
              the last thing announced before the chat route mounts.
              ``visuallyHidden`` from @mui/utils is the canonical sr-only
              style; inline-styled via ``style`` (not ``sx``) so the unitless
              1 / -1 values stay 1px, not theme-spacing units. */}
            <Box component="span" role="status" aria-live="polite" style={visuallyHidden}>
              {submitting
                ? t('srCheckingTypedAddress')
                : prefillState === 'resolving'
                  ? t('srCheckingAddress')
                  : prefillState === 'ready'
                    ? t('srAddressVerified')
                    : prefillState === 'failed'
                      ? t('srAddressNotVerified')
                      : ''}
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              // Faded + unclickable while the address is auto-typing or being
              // verified, so a half-typed or unverifiable address can't be
              // submitted. Enables once a valid address resolves.
              disabled={prefillBusy}
              aria-busy={submitting}
              aria-describedby={errorMessage ? 'landing-address-error' : undefined}
              // Faded while the typed address is checked; stays focusable.
              sx={[primaryCtaSx, submitting && { opacity: 0.7 }]}
            >
              {t('seeBallot')}
            </Button>
          </Box>

          {countyPreview.status !== 'hidden' && (
            <ButtonBase
              ref={locationButtonRef}
              onClick={handleUseLocation}
              // Stays focusable while locating so keyboard users keep their place.
              aria-disabled={locating || undefined}
              disableRipple
              sx={locationButtonSx}
            >
              {locating ? (
                <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} aria-hidden />
              ) : (
                <MyLocationIcon sx={{ fontSize: 18 }} aria-hidden />
              )}
              {t('locationUse')}
            </ButtonBase>
          )}
          <Box
            component="span"
            role="status"
            // Stays mounted while empty so screen readers announce the message.
            sx={{
              display: 'block',
              mt: locationMessage ? '8px' : 0,
              fontSize: 13,
              fontWeight: 500,
              textAlign: 'center',
              color: semanticColors.overlayWhite70,
            }}
          >
            {locationMessage}
          </Box>

          {/* Skip-for-now — a plain text link; hidden at md+ (desktop right
              pane), where the user is ALREADY in the chat. Inside the mobile
              ballot panel it stays, and hops to the Chat tab. */}
          <ButtonBase
            onClick={handleSkip}
            disableRipple
            sx={embedded ? { ...skipLinkSx, display: 'flex' } : skipLinkSx}
          >
            {t('skipChat')}
          </ButtonBase>
        </Box>
      </Box>

      {/* Footer: the privacy link alone, so the screen stays uncluttered.
          xs (standalone): a fixed-size flex sibling flush with the bottom, about
          as tall as the top row, so the content column centered between them
          sits mid-screen with equal space above and below.
          md+ (embedded desktop right pane): ``position: absolute`` takes it
          out of the flex chain so the centered column keeps the full height,
          matching ``ChatLandingState``'s centering envelope on the left pane. */}
      <Box
        sx={{
          flex: 'none',
          position: { xs: 'static', md: 'absolute' },
          left: { md: 0 },
          right: { md: 0 },
          bottom: { md: 0 },
          px: { xs: 0, md: '16px' },
          pt: '16px',
          // Phones: the link sits just above the home indicator (the mobile
          // panel already stops above the tab bar), so the slack goes to the
          // centered column instead of a dead band under the link.
          pb: {
            xs: embedded ? '12px' : 'max(12px, env(safe-area-inset-bottom))',
            md: '32px',
          },
          textAlign: 'center',
        }}
      >
        <ButtonBase
          component={RouterLink}
          to="/settings?section=privacy"
          onClick={(event) => {
            track('Privacy Choices Opened', { from: 'landing' });
            if (
              !isPhone &&
              settingsDialog &&
              !event.ctrlKey &&
              !event.metaKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              settingsDialog.openSettings('privacy');
            }
          }}
          disableRipple
          sx={{
            p: '4px 8px',
            fontFamily: 'inherit',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'underline',
            '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
          }}
        >
          {tCommon('privacyChoices.title')}
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default GuestLandingPage;
